import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-background-animation',
  standalone: true,
  templateUrl: './background-animation.component.html',
  styleUrl: './background-animation.component.scss',
})
export class BackgroundAnimationComponent implements AfterViewInit, OnDestroy {
  @ViewChild('techCanvas', { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;

  private ctx!: CanvasRenderingContext2D;
  private width = 0;
  private height = 0;
  private time = 0;
  private mouse = { x: null as number | null, y: null as number | null };
  private animationId = 0;
  private resizeObserver?: ResizeObserver;
  private hostParent: HTMLElement | null = null;
  private readonly isBrowser: boolean;

  private readonly onResize = () => this.syncCanvasSize();
  private readonly onMouseMove = (e: MouseEvent) => {
    if (!this.hostParent) return;
    const rect = this.hostParent.getBoundingClientRect();
    this.mouse.x = e.clientX - rect.left;
    this.mouse.y = e.clientY - rect.top;
  };
  private readonly onMouseLeave = () => {
    this.mouse.x = null;
    this.mouse.y = null;
  };

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;

    const canvas = this.canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    this.ctx = ctx;
    this.hostParent =
      (canvas.closest('.home-hero') as HTMLElement | null) ??
      (canvas.parentElement as HTMLElement | null) ??
      document.body;

    this.syncCanvasSize();
    this.animate();

    window.addEventListener('resize', this.onResize);
    this.hostParent.addEventListener('mousemove', this.onMouseMove);
    this.hostParent.addEventListener('mouseleave', this.onMouseLeave);

    this.resizeObserver = new ResizeObserver(() => this.syncCanvasSize());
    this.resizeObserver.observe(this.hostParent);
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) return;

    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.onResize);
    this.hostParent?.removeEventListener('mousemove', this.onMouseMove);
    this.hostParent?.removeEventListener('mouseleave', this.onMouseLeave);
    this.resizeObserver?.disconnect();
  }

  private isDarkMode(): boolean {
    return document.body.classList.contains('night-mode');
  }

  private syncCanvasSize(): void {
    if (!this.hostParent) return;

    const width = Math.max(1, this.hostParent.offsetWidth);
    const height = Math.max(1, this.hostParent.offsetHeight);
    if (Math.abs(width - this.width) < 1 && Math.abs(height - this.height) < 1) {
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    this.width = canvas.width = width;
    this.height = canvas.height = height;
  }

  private animate = (): void => {
    this.animationId = requestAnimationFrame(this.animate);
    this.syncCanvasSize();

    const ctx = this.ctx;
    const heroW = this.width;
    const heroH = this.height;
    if (!heroW || !heroH) return;

    ctx.clearRect(0, 0, heroW, heroH);
    this.time += 0.003;

    const isDark = this.isDarkMode();
    const colorGray = isDark ? '203, 213, 225' : '54, 95, 121';
    const colorOrange = '235, 96, 45';
    const lineAlpha = isDark ? 0.45 : 0.2;
    const barAlpha = isDark ? 0.2 : 0.08;
    const labelColor = isDark ? '#FFFFFF' : '#365F79';

    let activeBarPt: { x: number; y: number; h: number } | null = null;
    const barSpacing = 16;
    const totalBars = Math.floor(heroW / barSpacing);

    for (let i = 0; i < totalBars; i++) {
      const x = i * barSpacing;
      const h =
        Math.sin(i * 0.1 + this.time * 3) * Math.cos(i * 0.05 - this.time) * 40 + 60;
      const y = heroH - h;
      const dist = this.mouse.x != null ? Math.abs(this.mouse.x - x) : 9999;

      ctx.beginPath();
      ctx.moveTo(x, heroH);
      ctx.lineTo(x, y);

      if (dist < 80) {
        const intensity = 1 - dist / 80;
        ctx.strokeStyle = `rgba(${colorOrange}, ${intensity * 0.8})`;
        ctx.lineWidth = 3;
      } else {
        ctx.strokeStyle = `rgba(${colorGray}, ${barAlpha})`;
        ctx.lineWidth = 2;
      }
      ctx.stroke();

      if (this.mouse.x != null && dist <= barSpacing / 2) {
        activeBarPt = { x, y, h };
      }
    }

    const lines = [
      { amp: heroH * 0.15, freq: 0.002, speed: 1, offset: heroH * 0.35 },
      { amp: heroH * 0.1, freq: 0.003, speed: -1.5, offset: heroH * 0.5 },
      { amp: heroH * 0.2, freq: 0.0015, speed: 0.8, offset: heroH * 0.65 },
    ];

    const intersections: { x: number; y: number }[] = [];

    for (const line of lines) {
      ctx.beginPath();
      for (let x = 0; x < heroW; x += 5) {
        const y =
          Math.sin(x * line.freq + this.time * line.speed) * line.amp +
          Math.cos(x * line.freq * 2 - this.time * line.speed) * (line.amp * 0.2) +
          line.offset;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);

        if (this.mouse.x != null && Math.abs(this.mouse.x - x) <= 2.5) {
          intersections.push({ x, y });
        }
      }
      ctx.strokeStyle = `rgba(${colorGray}, ${lineAlpha})`;
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    if (this.mouse.x == null || this.mouse.y == null) return;

    const mx = this.mouse.x;
    const my = this.mouse.y;

    ctx.beginPath();
    ctx.moveTo(mx, 0);
    ctx.lineTo(mx, heroH);
    ctx.moveTo(0, my);
    ctx.lineTo(heroW, my);
    ctx.strokeStyle = `rgba(${colorOrange}, 0.4)`;
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);

    for (const pt of intersections) {
      const distY = Math.abs(my - pt.y);
      const isClose = distY < 60;
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, isClose ? 5 : 3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${colorOrange}, ${isClose ? 1 : 0.5})`;
      ctx.fill();

      if (isClose) {
        ctx.fillStyle = labelColor;
        ctx.font = "bold 12px 'Funnel Display', sans-serif";
        const fakeValue = (100 - (pt.y / heroH) * 100).toFixed(2);
        ctx.fillText(`VAL: ${fakeValue}`, pt.x + 12, pt.y - 12);
      }
    }

    if (activeBarPt) {
      const distY = Math.abs(my - activeBarPt.y);
      if (distY < 80) {
        ctx.beginPath();
        ctx.arc(activeBarPt.x, activeBarPt.y, 5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${colorOrange}, 1)`;
        ctx.fill();
        ctx.fillStyle = labelColor;
        ctx.font = "bold 12px 'Funnel Display', sans-serif";
        const fakeVol = `${Math.floor(activeBarPt.h * 14.2)}K`;
        ctx.fillText(`VOL: ${fakeVol}`, activeBarPt.x + 12, activeBarPt.y - 12);
      }
    }

    ctx.beginPath();
    ctx.arc(mx, my, 3, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(${colorOrange}, 1)`;
    ctx.stroke();
  };
}
